/** Skin Studio's first-run walkthrough: the steps; Skin Studio does the wiring. */
import type { Region } from '@ka/fun';
import type { WalkthroughStep } from '@ka/ui';

export interface SkinWalkthroughHost {
  /** What a doing step spotlights: the character, or the colour tray once a part is picked. */
  figure: () => Element | null | undefined;
  /** The name field and "Make this my character" box on the last step. */
  nameField: () => unknown;
}

/**
 * The parts each colouring step accepts: the first is the one it asks for; a tap on the others
 * counts too (the side of the head on "Tap the face"), and the step colours them all.
 */
export const STEP_REGIONS: Partial<Record<string, Region[]>> = {
  face: ['face', 'skin'],
  hair: ['hair'],
  eyes: ['eyes'],
  shirt: ['shirt', 'sleeves'],
  pants: ['pants'],
  shoes: ['shoes'],
};

export function skinSteps(host: SkinWalkthroughHost): WalkthroughStep[] {
  const colour = (id: string, say: string, hint = 'Then pick a color.'): WalkthroughStep => ({
    id,
    say,
    hint,
    target: host.figure,
  });
  return [
    {
      id: 'start',
      say: "Let's make your character!",
      hint: 'You can change anything later.',
      button: 'Start',
    },
    colour('face', 'Tap the face', 'Then pick a skin color.'),
    colour('hair', 'Tap the hair'),
    colour('eyes', 'Tap the eyes'),
    colour('shirt', 'Tap the shirt', 'Then pick a color you like.'),
    colour('pants', 'Tap the pants'),
    colour('shoes', 'Tap the shoes'),
    {
      id: 'spin',
      say: 'Spin your character around',
      hint: 'Drag it, or press Turn around.',
      target: host.figure,
    },
    {
      id: 'details',
      say: 'Want to draw details?',
      hint: 'Tap any part, then Draw on it.',
      button: 'Next',
    },
    {
      id: 'finish',
      say: 'You made a character!',
      hint: 'Give it a name.',
      button: 'Save it',
      waitForActivity: true,
      body: host.nameField,
    },
  ];
}
